import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useToast } from '@/hooks/use-toast';
import { Users, UserPlus, Settings, Crown } from 'lucide-react';

interface AdminUser {
  id: number;
  user_id: number;
  admin_role: string;
  assigned_by: number;
  assigned_at: string;
  is_active: boolean;
}

interface UserTier {
  id: number;
  user_id: number;
  tier_level: number;
  tier_name: string;
  assigned_by: number;
  assigned_at: string;
}

const AdminUserManager: React.FC = () => {
  const { toast } = useToast();
  const [adminUsers, setAdminUsers] = useState<AdminUser[]>([]);
  const [userTiers, setUserTiers] = useState<UserTier[]>([]);
  const [loading, setLoading] = useState(false);
  const [isAdminDialogOpen, setIsAdminDialogOpen] = useState(false);
  const [isTierDialogOpen, setIsTierDialogOpen] = useState(false);

  const [newAdmin, setNewAdmin] = useState({
    user_id: '',
    admin_role: 'Administrator'
  });

  const [newTier, setNewTier] = useState({
    user_id: '',
    tier_level: 1,
    tier_name: 'Basic'
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      await Promise.all([loadAdminUsers(), loadUserTiers()]);
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadAdminUsers = async () => {
    try {
      const { data, error } = await window.ezsite.apis.tablePage(15992, {
        PageNo: 1,
        PageSize: 100,
        OrderByField: "id",
        IsAsc: false,
        Filters: []
      });
      if (error) throw error;
      setAdminUsers(data.List || []);
    } catch (error) {
      console.error('Error loading admin users:', error);
    }
  };

  const loadUserTiers = async () => {
    try {
      const { data, error } = await window.ezsite.apis.tablePage(15216, {
        PageNo: 1,
        PageSize: 100,
        OrderByField: "id",
        IsAsc: false,
        Filters: []
      });
      if (error) throw error;
      setUserTiers(data.List || []);
    } catch (error) {
      console.error('Error loading user tiers:', error);
    }
  };

  const handleAddAdmin = async () => {
    if (!newAdmin.user_id) {
      toast({
        title: "Error",
        description: "Please enter a user ID",
        variant: "destructive"
      });
      return;
    }

    try {
      const { error } = await window.ezsite.apis.tableCreate(15992, {
        user_id: parseInt(newAdmin.user_id),
        admin_role: newAdmin.admin_role,
        assigned_by: 1,
        assigned_at: new Date().toISOString(),
        is_active: true
      });

      if (error) throw error;

      toast({
        title: "Success",
        description: "Administrator added successfully"
      });

      setNewAdmin({ user_id: '', admin_role: 'Administrator' });
      setIsAdminDialogOpen(false);
      loadAdminUsers();
    } catch (error) {
      console.error('Error adding admin:', error);
      toast({
        title: "Error",
        description: "Failed to add administrator",
        variant: "destructive"
      });
    }
  };

  const handleAddUserTier = async () => {
    if (!newTier.user_id) {
      toast({
        title: "Error",
        description: "Please enter a user ID",
        variant: "destructive"
      });
      return;
    }

    try {
      const { error } = await window.ezsite.apis.tableCreate(15216, {
        user_id: parseInt(newTier.user_id),
        tier_level: newTier.tier_level,
        tier_name: newTier.tier_name,
        assigned_by: 1,
        assigned_at: new Date().toISOString()
      });

      if (error) throw error;

      toast({
        title: "Success",
        description: "User tier assigned successfully"
      });

      setNewTier({ user_id: '', tier_level: 1, tier_name: 'Basic' });
      setIsTierDialogOpen(false);
      loadUserTiers();
    } catch (error) {
      console.error('Error adding user tier:', error);
      toast({
        title: "Error",
        description: "Failed to assign user tier",
        variant: "destructive"
      });
    }
  };

  const handleRemoveAdmin = async (adminId: number) => {
    try {
      const { error } = await window.ezsite.apis.tableUpdate(15992, {
        ID: adminId,
        is_active: false
      });

      if (error) throw error;

      toast({
        title: "Success",
        description: "Administrator removed successfully"
      });

      loadAdminUsers();
    } catch (error) {
      console.error('Error removing admin:', error);
      toast({
        title: "Error",
        description: "Failed to remove administrator",
        variant: "destructive"
      });
    }
  };

  return (
    <div className="space-y-6" data-id="atpr75nl5" data-path="src/components/admin/AdminUserManager.tsx">
      <div className="flex items-center justify-between" data-id="f9l8v2pzw" data-path="src/components/admin/AdminUserManager.tsx">
        <div data-id="xo2t39g9t" data-path="src/components/admin/AdminUserManager.tsx">
          <h2 className="text-2xl font-bold flex items-center gap-2" data-id="ev8vkgege" data-path="src/components/admin/AdminUserManager.tsx">
            <Users className="h-6 w-6" data-id="g7mpwh1hc" data-path="src/components/admin/AdminUserManager.tsx" />
            User Management
          </h2>
          <p className="text-muted-foreground" data-id="6ouzij0zc" data-path="src/components/admin/AdminUserManager.tsx">Manage administrators and user access levels</p>
        </div>
      </div>

      <Tabs defaultValue="admins" className="space-y-6" data-id="jjhq2yctl" data-path="src/components/admin/AdminUserManager.tsx">
        <TabsList className="grid w-full grid-cols-2" data-id="gv4zmwjj9" data-path="src/components/admin/AdminUserManager.tsx">
          <TabsTrigger value="admins" data-id="dghr4hbe7" data-path="src/components/admin/AdminUserManager.tsx">Administrators</TabsTrigger>
          <TabsTrigger value="tiers" data-id="2tx3tqkx8" data-path="src/components/admin/AdminUserManager.tsx">User Tiers</TabsTrigger>
        </TabsList>

        <TabsContent value="admins" className="space-y-6" data-id="hmpc7gvyb" data-path="src/components/admin/AdminUserManager.tsx">
          <div className="flex justify-between items-center" data-id="hsl0iks88" data-path="src/components/admin/AdminUserManager.tsx">
            <div data-id="ekm82jikq" data-path="src/components/admin/AdminUserManager.tsx">
              <h3 className="text-lg font-semibold" data-id="aplki9x06" data-path="src/components/admin/AdminUserManager.tsx">Administrator Management</h3>
              <p className="text-sm text-muted-foreground" data-id="0fseplm1j" data-path="src/components/admin/AdminUserManager.tsx">Grant and manage administrator privileges</p>
            </div>
            <Dialog open={isAdminDialogOpen} onOpenChange={setIsAdminDialogOpen} data-id="95dk2j1ah" data-path="src/components/admin/AdminUserManager.tsx">
              <DialogTrigger asChild data-id="9ojqhklz6" data-path="src/components/admin/AdminUserManager.tsx">
                <Button data-id="p8y9dsbd3" data-path="src/components/admin/AdminUserManager.tsx">
                  <UserPlus className="h-4 w-4 mr-2" data-id="1kc75dine" data-path="src/components/admin/AdminUserManager.tsx" />
                  Add Administrator
                </Button>
              </DialogTrigger>
              <DialogContent data-id="21quqlm5n" data-path="src/components/admin/AdminUserManager.tsx">
                <DialogHeader data-id="cb39542qe" data-path="src/components/admin/AdminUserManager.tsx">
                  <DialogTitle data-id="yrosyr7hc" data-path="src/components/admin/AdminUserManager.tsx">Add New Administrator</DialogTitle>
                  <DialogDescription data-id="n6wgehv0v" data-path="src/components/admin/AdminUserManager.tsx">
                    Grant administrator privileges to a user. All administrators have full access to system features.
                  </DialogDescription>
                </DialogHeader>
                <div className="space-y-4 py-4" data-id="ns4omisyn" data-path="src/components/admin/AdminUserManager.tsx">
                  <div className="space-y-2" data-id="amy10lugd" data-path="src/components/admin/AdminUserManager.tsx">
                    <Label htmlFor="admin-user-id" data-id="o9bq0xoxu" data-path="src/components/admin/AdminUserManager.tsx">User ID</Label>
                    <Input
                      id="admin-user-id"
                      placeholder="Enter user ID"
                      value={newAdmin.user_id}
                      onChange={(e) => setNewAdmin({ ...newAdmin, user_id: e.target.value })}
                      type="number" data-id="aztz4qzzn" data-path="src/components/admin/AdminUserManager.tsx" />

                  </div>
                  <div className="space-y-2" data-id="ru7oe6z6t" data-path="src/components/admin/AdminUserManager.tsx">
                    <Label htmlFor="admin-role" data-id="2vt3nwo0s" data-path="src/components/admin/AdminUserManager.tsx">Admin Role</Label>
                    <Select
                      value={newAdmin.admin_role}
                      onValueChange={(value) => setNewAdmin({ ...newAdmin, admin_role: value })} data-id="kkhwux1sm" data-path="src/components/admin/AdminUserManager.tsx">

                      <SelectTrigger data-id="wbqrcqxz8" data-path="src/components/admin/AdminUserManager.tsx">
                        <SelectValue data-id="j9p8lnjnb" data-path="src/components/admin/AdminUserManager.tsx" />
                      </SelectTrigger>
                      <SelectContent data-id="o2gfvp0td" data-path="src/components/admin/AdminUserManager.tsx">
                        <SelectItem value="Administrator" data-id="lmu0e1jpl" data-path="src/components/admin/AdminUserManager.tsx">Administrator</SelectItem>
                        <SelectItem value="System Administrator" data-id="wqr8zqtkq" data-path="src/components/admin/AdminUserManager.tsx">System Administrator</SelectItem>
                        <SelectItem value="Content Administrator" data-id="cs7aq4hl8" data-path="src/components/admin/AdminUserManager.tsx">Content Administrator</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="flex justify-end space-x-2" data-id="qst8wj27j" data-path="src/components/admin/AdminUserManager.tsx">
                  <Button variant="outline" onClick={() => setIsAdminDialogOpen(false)} data-id="3gn8zuqvf" data-path="src/components/admin/AdminUserManager.tsx">
                    Cancel
                  </Button>
                  <Button onClick={handleAddAdmin} data-id="5qimzfqg9" data-path="src/components/admin/AdminUserManager.tsx">
                    Add Administrator
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
          </div>

          <Card data-id="9ffh8sxdp" data-path="src/components/admin/AdminUserManager.tsx">
            <CardHeader data-id="n2hyixu7f" data-path="src/components/admin/AdminUserManager.tsx">
              <CardTitle data-id="0cogb9jx2" data-path="src/components/admin/AdminUserManager.tsx">Current Administrators</CardTitle>
              <CardDescription data-id="odwx2xx5z" data-path="src/components/admin/AdminUserManager.tsx">Users with administrative privileges</CardDescription>
            </CardHeader>
            <CardContent data-id="9647xf4rq" data-path="src/components/admin/AdminUserManager.tsx">
              <Table data-id="la8vjgfnq" data-path="src/components/admin/AdminUserManager.tsx">
                <TableHeader data-id="50q8yvtqc" data-path="src/components/admin/AdminUserManager.tsx">
                  <TableRow data-id="8afvy8pzm" data-path="src/components/admin/AdminUserManager.tsx">
                    <TableHead data-id="41ey5uapn" data-path="src/components/admin/AdminUserManager.tsx">User ID</TableHead>
                    <TableHead data-id="3sj40nybm" data-path="src/components/admin/AdminUserManager.tsx">Role</TableHead>
                    <TableHead data-id="ghwr0vx57" data-path="src/components/admin/AdminUserManager.tsx">Assigned Date</TableHead>
                    <TableHead data-id="q5lxjlisa" data-path="src/components/admin/AdminUserManager.tsx">Status</TableHead>
                    <TableHead data-id="oabs3qu08" data-path="src/components/admin/AdminUserManager.tsx">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody data-id="elnmuu68i" data-path="src/components/admin/AdminUserManager.tsx">
                  {adminUsers.map((admin) =>
                  <TableRow key={admin.id} data-id="hbykzy6le" data-path="src/components/admin/AdminUserManager.tsx">
                      <TableCell className="font-medium" data-id="6wqf0m07r" data-path="src/components/admin/AdminUserManager.tsx">#{admin.user_id}</TableCell>
                      <TableCell data-id="5nq3v8md7" data-path="src/components/admin/AdminUserManager.tsx">
                        <Badge variant="default" data-id="j1vn3btt9" data-path="src/components/admin/AdminUserManager.tsx">{admin.admin_role}</Badge>
                      </TableCell>
                      <TableCell data-id="f4lasrc94" data-path="src/components/admin/AdminUserManager.tsx">
                        {new Date(admin.assigned_at).toLocaleDateString()}
                      </TableCell>
                      <TableCell data-id="8r2lbbgw4" data-path="src/components/admin/AdminUserManager.tsx">
                        <Badge variant={admin.is_active ? "default" : "secondary"} data-id="ismp7nkxl" data-path="src/components/admin/AdminUserManager.tsx">
                          {admin.is_active ? "Active" : "Inactive"}
                        </Badge>
                      </TableCell>
                      <TableCell data-id="x2xdoaz77" data-path="src/components/admin/AdminUserManager.tsx">
                        {admin.is_active &&
                      <Button
                        variant="destructive"
                        size="sm"
                        onClick={() => handleRemoveAdmin(admin.id)} data-id="ps950wmlb" data-path="src/components/admin/AdminUserManager.tsx">

                            Remove
                          </Button>
                      }
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="tiers" className="space-y-6" data-id="ouhbvgizz" data-path="src/components/admin/AdminUserManager.tsx">
          <div className="flex justify-between items-center" data-id="l72wxi40x" data-path="src/components/admin/AdminUserManager.tsx">
            <div data-id="yx9zddlsp" data-path="src/components/admin/AdminUserManager.tsx">
              <h3 className="text-lg font-semibold" data-id="68ay4geo7" data-path="src/components/admin/AdminUserManager.tsx">User Tier Management</h3>
              <p className="text-sm text-muted-foreground" data-id="l7fmzhtkh" data-path="src/components/admin/AdminUserManager.tsx">Manage user access levels and privileges</p>
            </div>
            <Dialog open={isTierDialogOpen} onOpenChange={setIsTierDialogOpen} data-id="j3modjffu" data-path="src/components/admin/AdminUserManager.tsx">
              <DialogTrigger asChild data-id="5lolg5s7a" data-path="src/components/admin/AdminUserManager.tsx">
                <Button data-id="xobwcymg1" data-path="src/components/admin/AdminUserManager.tsx">
                  <Crown className="h-4 w-4 mr-2" data-id="3ob3rhj2l" data-path="src/components/admin/AdminUserManager.tsx" />
                  Assign Tier
                </Button>
              </DialogTrigger>
              <DialogContent data-id="19nfq5mkl" data-path="src/components/admin/AdminUserManager.tsx">
                <DialogHeader data-id="bcnrlpj1u" data-path="src/components/admin/AdminUserManager.tsx">
                  <DialogTitle data-id="uumgj6cpc" data-path="src/components/admin/AdminUserManager.tsx">Assign User Tier</DialogTitle>
                  <DialogDescription data-id="wy8kmtw72" data-path="src/components/admin/AdminUserManager.tsx">
                    Set the access level for a user. Higher tiers provide more features and privileges.
                  </DialogDescription>
                </DialogHeader>
                <div className="space-y-4 py-4" data-id="e9nb9neds" data-path="src/components/admin/AdminUserManager.tsx">
                  <div className="space-y-2" data-id="5og1e0ke1" data-path="src/components/admin/AdminUserManager.tsx">
                    <Label htmlFor="tier-user-id" data-id="rcu09ro4r" data-path="src/components/admin/AdminUserManager.tsx">User ID</Label>
                    <Input
                      id="tier-user-id"
                      placeholder="Enter user ID"
                      value={newTier.user_id}
                      onChange={(e) => setNewTier({ ...newTier, user_id: e.target.value })}
                      type="number" data-id="qb4pb83kk" data-path="src/components/admin/AdminUserManager.tsx" />

                  </div>
                  <div className="space-y-2" data-id="0dli66reg" data-path="src/components/admin/AdminUserManager.tsx">
                    <Label htmlFor="tier-level" data-id="qm7amw5xc" data-path="src/components/admin/AdminUserManager.tsx">Tier Level</Label>
                    <Select
                      value={newTier.tier_level.toString()}
                      onValueChange={(value) => {
                        const level = parseInt(value);
                        setNewTier({
                          ...newTier,
                          tier_level: level,
                          tier_name: level === 2 ? 'Premium' : 'Basic'
                        });
                      }} data-id="z1l6crozl" data-path="src/components/admin/AdminUserManager.tsx">

                      <SelectTrigger data-id="e1scr6kqa" data-path="src/components/admin/AdminUserManager.tsx">
                        <SelectValue data-id="wq87qlq32" data-path="src/components/admin/AdminUserManager.tsx" />
                      </SelectTrigger>
                      <SelectContent data-id="s4tobrb6p" data-path="src/components/admin/AdminUserManager.tsx">
                        <SelectItem value="1" data-id="zdnr46bf0" data-path="src/components/admin/AdminUserManager.tsx">Level 1 - Basic</SelectItem>
                        <SelectItem value="2" data-id="5qg8sot9b" data-path="src/components/admin/AdminUserManager.tsx">Level 2 - Premium</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="flex justify-end space-x-2" data-id="rcvcmlfcp" data-path="src/components/admin/AdminUserManager.tsx">
                  <Button variant="outline" onClick={() => setIsTierDialogOpen(false)} data-id="t9doefnkd" data-path="src/components/admin/AdminUserManager.tsx">
                    Cancel
                  </Button>
                  <Button onClick={handleAddUserTier} data-id="nfvtgwrxn" data-path="src/components/admin/AdminUserManager.tsx">
                    Assign Tier
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
          </div>

          <Card data-id="n2ue16y6j" data-path="src/components/admin/AdminUserManager.tsx">
            <CardHeader data-id="tiyqxjb5o" data-path="src/components/admin/AdminUserManager.tsx">
              <CardTitle data-id="6mdjpnfus" data-path="src/components/admin/AdminUserManager.tsx">User Tiers</CardTitle>
              <CardDescription data-id="zd35b8wdq" data-path="src/components/admin/AdminUserManager.tsx">Current user access levels and privileges</CardDescription>
            </CardHeader>
            <CardContent data-id="ptfnnqqk5" data-path="src/components/admin/AdminUserManager.tsx">
              <Table data-id="wmveh13bg" data-path="src/components/admin/AdminUserManager.tsx">
                <TableHeader data-id="bdwkkxoga" data-path="src/components/admin/AdminUserManager.tsx">
                  <TableRow data-id="c7ddfef57" data-path="src/components/admin/AdminUserManager.tsx">
                    <TableHead data-id="m6u076hc6" data-path="src/components/admin/AdminUserManager.tsx">User ID</TableHead>
                    <TableHead data-id="8pg7rsx8u" data-path="src/components/admin/AdminUserManager.tsx">Tier Level</TableHead>
                    <TableHead data-id="n16e19szr" data-path="src/components/admin/AdminUserManager.tsx">Tier Name</TableHead>
                    <TableHead data-id="wow2l54qi" data-path="src/components/admin/AdminUserManager.tsx">Assigned Date</TableHead>
                    <TableHead data-id="vim1fg0ee" data-path="src/components/admin/AdminUserManager.tsx">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody data-id="ntdql27fg" data-path="src/components/admin/AdminUserManager.tsx">
                  {userTiers.map((tier) =>
                  <TableRow key={tier.id} data-id="815djn276" data-path="src/components/admin/AdminUserManager.tsx">
                      <TableCell className="font-medium" data-id="6iai7wfp2" data-path="src/components/admin/AdminUserManager.tsx">#{tier.user_id}</TableCell>
                      <TableCell data-id="dbdirvz2t" data-path="src/components/admin/AdminUserManager.tsx">{tier.tier_level}</TableCell>
                      <TableCell data-id="paeb3tpw8" data-path="src/components/admin/AdminUserManager.tsx">
                        <Badge variant={tier.tier_level === 2 ? "default" : "secondary"} data-id="78hdxfs5q" data-path="src/components/admin/AdminUserManager.tsx">
                          {tier.tier_name}
                        </Badge>
                      </TableCell>
                      <TableCell data-id="auve3xny4" data-path="src/components/admin/AdminUserManager.tsx">
                        {new Date(tier.assigned_at).toLocaleDateString()}
                      </TableCell>
                      <TableCell data-id="qfaqozrrk" data-path="src/components/admin/AdminUserManager.tsx">
                        <Button variant="outline" size="sm" data-id="f2hyb4nv7" data-path="src/components/admin/AdminUserManager.tsx">
                          Edit
                        </Button>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>);

};

export default AdminUserManager;